// Live order updates from the middleware's Server-Sent Events stream. EventSource can't send an
// Authorization header, so this reads the stream with fetch and reconnects itself.

const FIRST_RETRY_MS = 2000;
const MAX_RETRY_MS = 60000;

// Splits the stream into "event:" / "data:" frames and hands each "order" frame to onOrder
function parseFrames(buffer, onOrder) {
  let rest = buffer;
  let end;
  while ((end = rest.indexOf("\n\n")) !== -1) {
    const frame = rest.slice(0, end);
    rest = rest.slice(end + 2);
    const event = frame.match(/^event: (.*)$/m)?.[1];
    const data = frame.match(/^data: (.*)$/m)?.[1];
    if (event !== "order" || !data) continue;
    try {
      const order = JSON.parse(data);
      if (order && Number.isSafeInteger(order.id) && Array.isArray(order.items)) onOrder(order);
    } catch {
      // a malformed frame is skipped; the next update carries the full order again
    }
  }
  return rest;
}

/**
 * Starts listening and returns a function that stops it. A 401 stops for good (the session has
 * ended); anything else, including a dropped connection, retries with a growing delay.
 */
export function subscribeToOrderUpdates({ url, token, onOrder }) {
  let stopped = false;
  let retryMs = FIRST_RETRY_MS;
  let retryTimer = null;
  let controller = null;

  const scheduleRetry = () => {
    if (stopped) return;
    retryTimer = setTimeout(connect, retryMs);
    retryMs = Math.min(retryMs * 2, MAX_RETRY_MS);
  };

  async function connect() {
    controller = new AbortController();
    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" },
        signal: controller.signal,
        cache: "no-store",
      });
      if (response.status === 401) return;
      if (!response.ok || !response.body) {
        scheduleRetry();
        return;
      }

      retryMs = FIRST_RETRY_MS;
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer = parseFrames(buffer + decoder.decode(value, { stream: true }), onOrder);
      }
      scheduleRetry();
    } catch {
      scheduleRetry();
    }
  }

  connect();
  return () => {
    stopped = true;
    clearTimeout(retryTimer);
    if (controller) controller.abort();
  };
}
